import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import './App.css';
import NavBar from './components/NavBar';
import Banner from './components/Banner';
import Skills from './components/Skills'
import Experience from './components/Experience';
import Projects from './components/Projects'
import Apps from './components/Apps'
import Footer from './components/Footer';
import Contact from './components/Contact'
import LanguageSwitcher from './components/LanguageSwitcher';
import ParticlesBackground from './components/ParticlesBackground';
import VideoBackground from './components/VideoBackground';
import ScrollProgress from './components/ScrollProgress';
import CursorFollower from './components/CursorFollower';
import ScrollToTop from './components/ScrollToTop';
import RadioPlayer from './components/RadioPlayer';
import useComixReveal from './hooks/useComixReveal';
import useCubeReveal from './hooks/useCubeReveal';

import 'bootstrap/dist/css/bootstrap.min.css';

const SITE_URL = 'https://facucorona.dev.ar';
const OG_IMAGE = `${SITE_URL}/og-capture.jpeg`;

const APP_SCHEMA_CATEGORIES = {
  todo: 'ProductivityApplication',
  metronome: 'MultimediaApplication',
  weather: 'UtilitiesApplication',
  pomodoro: 'ProductivityApplication',
  seo: 'BusinessApplication',
  timer: 'ProductivityApplication',
  persia: 'GameApplication',
  oracle: 'EntertainmentApplication',
  guard: 'BusinessApplication',
  trends: 'BusinessApplication',
  guitar: 'MultimediaApplication',
  circle: 'EducationalApplication',
  uplink: 'GameApplication',
  kabbalah: 'EducationalApplication',
  stats: 'BusinessApplication',
  socialstats: 'BusinessApplication',
  illuminati: 'GameApplication',
  truco: 'GameApplication',
};

const APP_IDS = Object.keys(APP_SCHEMA_CATEGORIES);

function App() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'en';

  const [bgVideo, setBgVideo] = useState({
    videoId: 'liFjsSDj1eM',
    startTime: 10,
    isPlaying: false,
    isMuted: true,
    volume: 0,
  });

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    document.body.classList.toggle('bg-video-playing', !!bgVideo.isPlaying);
  }, [bgVideo.isPlaying]);

  useComixReveal();
  useCubeReveal();

  const personId = `${SITE_URL}/#person`;

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: t('pageTitle'),
    url: SITE_URL,
    inLanguage: ['es', 'en'],
    publisher: { '@id': personId },
  };

  const profileJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': `${SITE_URL}/#profile`,
    mainEntity: { '@id': personId },
    name: t('pageTitle'),
    about: t('metaDescription'),
    inLanguage: lang === 'es' ? 'es' : 'en',
  };

  const appsJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${SITE_URL}/#apps`,
    name: t('apps.title'),
    itemListElement: APP_IDS.map((id, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'WebApplication',
        name: t(`apps.${id}.title`),
        description: t(`apps.${id}.description`),
        url: `${SITE_URL}/#apps`,
        applicationCategory: APP_SCHEMA_CATEGORIES[id],
        operatingSystem: 'Web',
        inLanguage: ['es', 'en'],
        author: { '@id': personId },
      },
    })),
  };

  return (
    <HelmetProvider>
      <Helmet>
        <html lang={lang} />
        <title>{t('pageTitle')}</title>
        <meta name="description" content={t('metaDescription')} />
        <meta name="robots" content="index, follow" />

        <link rel="canonical" href={SITE_URL} />
        <link rel="alternate" hreflang="es" href={SITE_URL} />
        <link rel="alternate" hreflang="en" href={SITE_URL} />
        <link rel="alternate" hreflang="x-default" href={SITE_URL} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:title" content={t('pageTitle')} />
        <meta property="og:description" content={t('metaDescription')} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('pageTitle')} />
        <meta name="twitter:description" content={t('metaDescription')} />
        <meta name="twitter:image" content={OG_IMAGE} />

        <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(profileJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(appsJsonLd)}</script>
      </Helmet>
      <div className="App">
        <a href="#main" className="skip-link">{t('nav.skipToContent')}</a>
        <ScrollProgress />
        <CursorFollower />
        <VideoBackground 
          videoId={bgVideo.videoId}
          startTime={bgVideo.startTime}
          isPlaying={bgVideo.isPlaying}
          isMuted={bgVideo.isMuted}
          volume={bgVideo.volume}
        />
        <ParticlesBackground />
        <NavBar />
        <main id="main">
          <Banner />
          <Skills />
          <Experience />
          <Projects />
          <Apps />
          <Contact />
          <Footer />
        </main>
        <ScrollToTop />
        <LanguageSwitcher />
        <RadioPlayer 
          bgVideo={bgVideo}
          onBgVideoChange={setBgVideo}
        />
      </div>
     </HelmetProvider>
  );
}

export default App;
